import React from "react";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { StatusColumn } from "../common";
import { ActionDropdown } from "./ActionDropdown";
import ActionTakenBadge from "./ActionTakenBadge";

const ORepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: "O Rep",
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "sales_rep_user_id",
    "sales_rep_first_name",
    "sales_rep_last_name",
    "sales_rep_avatar_file_id",
  ],
  accessorKey: "sales_rep",
  canSort: true,
  orderByField: "sales_rep_last_name",
  width: 73,
  dataTransform: ({
    sales_rep_first_name,
    sales_rep_last_name,
    sales_rep_avatar_file_id,
  }) => ({
    full_name:
      `${sales_rep_first_name || ""} ${sales_rep_last_name || ""}`.trim() ||
      "Unknown Rep",
    avatar_file_id: sales_rep_avatar_file_id,
  }),
};

const CRepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: "C Rep",
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "client_rep_id",
    "client_rep_first_name",
    "client_rep_last_name",
    "client_rep_avatar_file_id",
  ],
  accessorKey: "client_rep",
  canSort: true,
  orderByField: "client_rep_last_name",
  width: 73,
  dataTransform: ({
    client_rep_first_name,
    client_rep_last_name,
    client_rep_avatar_file_id,
  }) => ({
    full_name:
      `${client_rep_first_name || ""} ${client_rep_last_name || ""}`.trim() ||
      "Unknown Rep",
    avatar_file_id: client_rep_avatar_file_id,
  }),
};

const ProjectNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Text
> = {
  header: "Project",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["order_id", "job_number"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 80,
  dataTransform: ({ job_number }) => ({ text: `#${job_number}` }),
};

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> =
  {
    header: "Name",
    type: BASE_COLUMN_TYPES.Link,
    fields: ["job_name", "job_number"],
    accessorKey: "job_name",
    canSort: true,
    orderByField: "job_name",
    width: 150,
    textOptions: {
      truncate: true,
    },
    dataTransform: ({ job_name, job_number }) => ({
      href: `/project/${job_number}`,
      text: job_name,
    }),
  };

const ClientColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Client",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["client_name"],
  accessorKey: "client",
  canSort: true,
  orderByField: "client_name",
  width: 150,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ client_name }) => ({
    text: client_name,
  }),
};

const BudgetColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Budget or Total",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["job_total_budget"],
  accessorKey: "job_total_budget",
  canSort: true,
  orderByField: "job_total_budget",
  width: 75,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ job_total_budget, currency_id }) => ({
    amount: job_total_budget,
    currency_id,
  }),
};

export const DateCreatedColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: "Created",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["job_date_created"],
  accessorKey: "job_date_created",
  canSort: true,
  orderByField: "job_date_created",
  width: 120,
};

const InHandsDateColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> =
  {
    header: "In-Hands",
    type: BASE_COLUMN_TYPES.Date,
    fields: ["job_date_inhandsdate"],
    accessorKey: "job_date_inhandsdate",
    canSort: true,
    orderByField: "job_date_inhandsdate",
    width: 100,
  };

const ActionTakenColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: "Action Taken",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["project_actions", "dismissed_by", "order_id", "client_id"],
  accessorKey: "project_actions",
  orderByField: "project_action_combined",
  canSort: true,
  width: 170,
  dataTransform: ({ project_actions, dismissed_by, order_id, client_id }) => ({
    cell: (
      <ActionTakenBadge
        projectAction={project_actions}
        dismissed={!!dismissed_by}
        order_id={order_id}
        client_id={client_id}
      />
    ),
  }),
};

const ActionsColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> =
  {
    header: "Actions",
    type: BASE_COLUMN_TYPES.Generic,
    fields: [
      "dismissed_by",
      "order_id",
      "job_id",
      "client_id",
      "client_name",
      "job_number",
      "job_total_budget",
      "event_type_id",
      "client_id",
      "order_type",
      "billing_contact_id",
      "job_id",
      "public_view_layout_id",
      "public_view_template_id",
      "view_type"
    ],
    accessorKey: "dismissed_by",
    canSort: false,
    width: 170,
    justify: "center",
    dataTransform: ({
      dismissed_by,
      order_id,
      job_id,
      client_id,
      client_name,
      job_number,
      job_total_budget,
      event_type_id,
      order_type,
      billing_contact_id,
      public_view_layout_id,
      public_view_template_id,
      view_type
    }) => ({
      cell: (
        <ActionDropdown
          order_id={order_id}
          dismissed={!!dismissed_by}
          job_id={job_id}
          client_id={client_id}
          client_name={client_name}
          job_number={job_number}
          job_total_budget={job_total_budget}
          event_type_id={event_type_id}
          order_type={order_type}
          billing_contact_id={billing_contact_id}
          public_view_layout_id={public_view_layout_id}
          public_view_template_id={public_view_template_id}
          view_type={view_type}
        />
      ),
    }),
  };

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ORepColumn,
  CRepColumn,
  ProjectNumberColumn,
  ProjectNameColumn,
  ClientColumn,
  StatusColumn,
  BudgetColumn,
  DateCreatedColumn,
  InHandsDateColumn,
  ActionTakenColumn,
  ActionsColumn,
];
